import React, { lazy, Suspense } from 'react';
import { createBrowserRouter, RouterProvider, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react';
import { AppLayout, InactivityScreen, ProtectedRoute, SignInPrompt } from '../components';
import { FeatureLoadError, LoadingSpinner, RouteErrorBoundary, RootErrorElement } from '../components/common';
import { COMPONENT_IDS, PAGE_PATHS } from '../features/app-config/constants/appConstants';
import { useAppConfig } from '../features/app-config';
import { InactivityProvider, useInactivity } from '../providers';
import { isProduction } from '../config/environment';

const Dashboard = lazy(() => import('../pages/Dashboard').then((module) => ({ default: module.Dashboard })));
const Onboarding = lazy(() => import('../pages/Onboarding').then((module) => ({ default: module.Onboarding })));
const DebugPage = lazy(() => import('../pages/Debug').then((module) => ({ default: module.DebugPage })));
const ComponentsPage = lazy(() => import('../pages/Components').then((module) => ({ default: module.ComponentsPage })));
const TesterAidPage = lazy(() => import('../pages/TesterAid').then((module) => ({ default: module.TesterAidPage })));
const RobinChat = lazy(() => import('../pages/RobinChat').then((module) => ({ default: module.RobinChat })));
const NotFound = lazy(() => import('../pages/NotFound').then((module) => ({ default: module.NotFound })));

const LegacyInsightsHubCardRedirect: React.FC = () => {
  const { id } = useParams<{ id: string }>();

  if (!id) {
    return <Navigate to={`${PAGE_PATHS.DASHBOARD}?panel=${COMPONENT_IDS.INSIGHTS_HUB_PANEL}`} replace />;
  }

  return (
    <Navigate
      to={`${PAGE_PATHS.DASHBOARD}?panel=${COMPONENT_IDS.INSIGHTS_HUB_PANEL}&overview-id=${encodeURIComponent(id)}`}
      replace
    />
  );
};

const AppContent: React.FC = () => {
  const { isInactive } = useInactivity();
  const { error, onRetry, isLoading, needsOnboarding } = useAppConfig();

  if (error && !isLoading) {
    return <FeatureLoadError error={error} onRetry={onRetry} />;
  }

  if (!isLoading && needsOnboarding) {
    return (
      <RouteErrorBoundary>
        <Suspense fallback={<LoadingSpinner />}>
          <Onboarding />
        </Suspense>
      </RouteErrorBoundary>
    );
  }

  return (
    <>
      <AppLayout>
        <RouteErrorBoundary>
          <Suspense fallback={<LoadingSpinner />}>
            <Routes>
              <Route path="/" element={<Navigate to={PAGE_PATHS.DASHBOARD} replace />} />
              <Route
                path={PAGE_PATHS.DASHBOARD}
                element={<ProtectedRoute element={<Dashboard />} path={PAGE_PATHS.DASHBOARD} />}
              />
              <Route
                path={PAGE_PATHS.ROBIN_CHAT}
                element={<ProtectedRoute element={<RobinChat />} path={PAGE_PATHS.ROBIN_CHAT} />}
              />
              <Route
                path={PAGE_PATHS.INSIGHTS_HUB}
                element={
                  <ProtectedRoute
                    element={
                      <Navigate to={`${PAGE_PATHS.DASHBOARD}?panel=${COMPONENT_IDS.INSIGHTS_HUB_PANEL}`} replace />
                    }
                    path={PAGE_PATHS.INSIGHTS_HUB}
                  />
                }
              />
              <Route
                path={PAGE_PATHS.INSIGHTS_HUB_CARD}
                element={<ProtectedRoute element={<LegacyInsightsHubCardRedirect />} path={PAGE_PATHS.INSIGHTS_HUB} />}
              />
              {!isProduction() && (
                <>
                  <Route
                    path={PAGE_PATHS.DEBUG}
                    element={<ProtectedRoute element={<DebugPage />} path={PAGE_PATHS.DEBUG} />}
                  />
                  <Route
                    path={PAGE_PATHS.COMPONENTS}
                    element={<ProtectedRoute element={<ComponentsPage />} path={PAGE_PATHS.COMPONENTS} />}
                  />
                  <Route
                    path={PAGE_PATHS.TESTER_AID}
                    element={<ProtectedRoute element={<TesterAidPage />} path={PAGE_PATHS.TESTER_AID} />}
                  />
                </>
              )}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </RouteErrorBoundary>
      </AppLayout>

      {isInactive && <InactivityScreen />}
    </>
  );
};

const basePath = ((import.meta.env.VITE_BASE_PATH as string) || '/').replace(/\/+$/, '') || '/';

const RootElement: React.FC = () => (
  <>
    <AuthenticatedTemplate>
      <InactivityProvider>
        <AppContent />
      </InactivityProvider>
    </AuthenticatedTemplate>
    <UnauthenticatedTemplate>
      <SignInPrompt />
    </UnauthenticatedTemplate>
  </>
);

const router = createBrowserRouter(
  [{ path: '*', element: <RootElement />, errorElement: <RootErrorElement /> }],
  { basename: basePath },
);

export const AppRouter: React.FC = () => {
  return <RouterProvider router={router} />;
};
